import React from 'react';
import { appConfig } from 'config/appConfig';
import { parseEmailStatementError } from 'utils/emailErrorParser';
import { usePhysicianEmailStatement } from './usePhysicianEmailStatement';

export interface useEmailStatementProps {
  onSent?: (id: string) => void;
}

export const useEmailStatement = ({ onSent }: useEmailStatementProps = {}) => {
  const isStatementEnabled = appConfig.isStatementEnabled;
  const [toastOpen, setToastOpen] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState('');
  const [toastSeverity, setToastSeverity] = React.useState<'error' | 'warning' | 'success'>('error');
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);
  const [emailRecipient, setEmailRecipient] = React.useState<{ id: string; name: string } | null>(null);

  const { requestEmailStatementAsync, isLoading } = usePhysicianEmailStatement({
    onSuccess: () => {
      setToastMessage('Email statement sent successfully!');
      setToastSeverity('success');
      setToastOpen(true);
    },
    onError: error => {
      const parsedError = parseEmailStatementError(error);
      setToastMessage(parsedError.message);
      setToastSeverity(parsedError.severity);
      setToastOpen(true);
    },
  });

  const openEmailDialog = React.useCallback((physicianId: string, physicianName: string) => {
    setEmailRecipient({ id: physicianId, name: physicianName });
    setEmailDialogOpen(true);
  }, []);

  const handleEmailConfirm = async (captchaToken: string) => {
    if (!emailRecipient) return;
    try {
      await requestEmailStatementAsync({
        physicianId: emailRecipient.id,
        recaptchaToken: captchaToken,
      });
      onSent?.(emailRecipient.id);
    } catch (error) {
      console.error('Failed to send email statement:', error);
    } finally {
      setEmailDialogOpen(false);
      setEmailRecipient(null);
    }
  };

  const handleEmailDialogClose = () => {
    setEmailDialogOpen(false);
    setEmailRecipient(null);
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  return {
    isStatementEnabled,
    isLoading,
    toastOpen,
    toastMessage,
    toastSeverity,
    emailDialogOpen,
    emailRecipient,
    openEmailDialog,
    handleEmailConfirm,
    handleEmailDialogClose,
    handleToastClose,
  };
};
